import { ReactNode, useRef } from 'react';

import Button from 'components/ui/button';
import CartoesCarregando from 'components/ui/cartoes-carregando';
import PageHeader from 'components/ui/page-header';
import Paginacao from 'components/ui/paginacao';
import { useAuth } from 'context/AuthContext';
import { ROTA_DO_FEED } from '../rotas-do-feed';
import { EstadoDaLista } from '../useListaDoPrefixo';
import PilulaDoCiclo from './PilulaDoCiclo';

interface PaginaDoFeedProps {
  titulo: string;
  estado: EstadoDaLista;
  textoDoErro: string;
  textoDoVazio: string;
  onTentarDeNovo: () => void;
  /** Linha acima da lista ("1.381 reconhecimentos"), anunciada ao leitor de tela; some enquanto carrega. */
  resumo?: string;
  /** Filtros entre o cabeçalho e a lista. */
  filtros?: ReactNode;
  totalDePaginas: number;
  pagina: number;
  onPagina: (pagina: number) => void;
  rotuloDaPaginacao: string;
  /** A lista, mostrada só no estado `pronto`. */
  children: ReactNode;
}

/**
 * Casca das páginas "Ver todos do prefixo" do Feed (AJ3-06, AJ3-07, AJ3-12, AJ3-13): voltar ao Feed, título,
 * pílula do ciclo do app, estados de carregando, erro e vazio, e a paginação. A troca de página e o "Tentar de novo"
 * levam o foco ao título: a paginação e o botão somem enquanto a página carrega, e o foco não pode cair no `body`.
 */
export default function PaginaDoFeed({
  titulo,
  estado,
  textoDoErro,
  textoDoVazio,
  onTentarDeNovo,
  resumo,
  filtros,
  totalDePaginas,
  pagina,
  onPagina,
  rotuloDaPaginacao,
  children,
}: PaginaDoFeedProps) {
  const { cycleSelected } = useAuth();
  const tituloRef = useRef<HTMLHeadingElement>(null);

  function tentarDeNovo() {
    onTentarDeNovo();
    tituloRef.current?.focus({ preventScroll: true });
  }

  function trocarDePagina(nova: number) {
    onPagina(nova);
    tituloRef.current?.focus({ preventScroll: true });
    tituloRef.current?.scrollIntoView?.({ block: 'start' });
  }

  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
      <PageHeader
        ref={tituloRef}
        titulo={titulo}
        voltar={{ to: ROTA_DO_FEED, destino: 'o Feed Joga Junto' }}
        acoes={cycleSelected?.title && <PilulaDoCiclo titulo={cycleSelected.title} />}
      />

      {filtros}

      {/*
        Região viva sempre no DOM (vazia, ela não ocupa espaço): o leitor de tela ouve o total quando a lista chega e o
        vazio quando o filtro não acha nada. Uma região que aparece já com o texto costuma não ser anunciada.
      */}
      <div aria-live="polite">
        {estado === 'pronto' && resumo && <p className="m-0 mb-3 text-[12px] text-content">{resumo}</p>}
        {estado === 'vazio' && (
          <p className="m-0 rounded-jj-lg bg-brand-white px-[26px] py-6 text-[13px] text-content shadow-jj-01">{textoDoVazio}</p>
        )}
      </div>

      {estado === 'carregando' && <CartoesCarregando />}

      {estado === 'erro' && (
        <div className="flex flex-wrap items-center gap-4 rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
          <p className="m-0 flex-1 text-[13px] text-content">{textoDoErro}</p>
          <Button variant="solido" onClick={tentarDeNovo}>
            Tentar de novo
          </Button>
        </div>
      )}

      {estado === 'pronto' && (
        <>
          {children}
          <Paginacao total={totalDePaginas} atual={pagina} onChange={trocarDePagina} rotulo={rotuloDaPaginacao} />
        </>
      )}
    </div>
  );
}
